import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    DestroyRef,
    ElementRef,
    HostBinding,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ElementProvider } from '@frontend/sports/common/core/utils/element-provider';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { LOADING_INDICATOR, LoadingIndicatorComponent } from '@frontend/sports/loading-indicator-feature';
import { MODAL_DIALOG_CONTAINER } from '@frontend/sports/modal/feature';
import { filter, merge } from 'rxjs';

import { ColumnLayoutService } from './column-layout.service';
import { ColumnComponentRegistration, ColumnComponentRemoval, LayoutColumn } from './layout.model';

interface IContainerContents {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    componentRef: ComponentRef<any>;
    indicatorRef: ComponentRef<LoadingIndicatorComponent>;
}

@Component({
    selector: 'ms-column',
    template: ` <div #columnContainer class="column-container"><ng-container #container /></div> `,
    providers: [ScrollContainerService],
})
export class ColumnComponent implements AfterViewInit, OnInit, OnDestroy {
    private layoutManager = inject(ColumnLayoutService);
    private changeDetector = inject(ChangeDetectorRef);
    private injector = inject(Injector);
    private scrollContainer = inject(ScrollContainerService);

    @Input() location: LayoutColumn;
    @HostBinding('class') className = 'column-wrapper';
    @ViewChild('columnContainer', { static: true }) columnContainerRef: ElementRef;
    @ViewChild('container', { read: ViewContainerRef, static: true })
    containerRef: ViewContainerRef;

    private requestedComponents: ColumnComponentRegistration[] = [];
    private columnContainerProvider: ElementProvider;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly componentInstances: Map<any, IContainerContents> = new Map<any, IContainerContents>(); // <TYPE, INSTANCE>
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.requestedComponents = this.layoutManager.layout.registeredComponents().filter((c) => c.location === this.location);

        merge(this.layoutManager.componentRegistered, this.layoutManager.componentUpdated$)
            .pipe(
                filter((c) => c.location === this.location),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((component) => this.initializeComponent(component));

        this.layoutManager.componentRemoved
            .pipe(
                filter((c) => c.location === this.location),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((component) => this.removeComponent(component));
    }

    ngAfterViewInit(): void {
        if (!this.containerRef || !this.columnContainerRef) {
            return;
        }

        this.columnContainerProvider = new ElementProvider(this.columnContainerRef);

        if (this.requestedComponents.length > 0) {
            this.requestedComponents.forEach(this.initOrUpdateComponent);
        }

        this.scrollContainer.setTarget(this.columnContainerRef.nativeElement.parentElement);

        this.changeDetector.detectChanges();
    }

    ngOnDestroy(): void {
        this.unregisterComponentInstances();
    }

    private initializeComponent = (component: ColumnComponentRegistration | undefined) => {
        if (!component) {
            return;
        }
        if (!this.containerRef) {
            this.requestedComponents.push(component);

            return;
        }

        this.initOrUpdateComponent(component);
        this.changeDetector.detectChanges();
    };

    private initOrUpdateComponent = (data: ColumnComponentRegistration): void => {
        let containerContents = this.componentInstances.get(data.component);
        if (!containerContents) {
            containerContents = this.createComponent(data);
        }

        if (data.inputs) {
            Object.keys(data.inputs).forEach((key) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                containerContents!.componentRef.instance[key] = data.inputs[key];
            });
        }
    };

    private createComponent = (data: ColumnComponentRegistration): IContainerContents => {
        const injector = this.createCustomInjector();
        const componentRef = this.containerRef.createComponent(data.component, { index: 0, injector });
        const indicatorRef = this.createIndicator(injector);

        const containerContents: IContainerContents = {
            componentRef,
            indicatorRef,
        };

        this.componentInstances.set(data.component, containerContents);

        return containerContents;
    };

    private createCustomInjector = (): Injector =>
        Injector.create({
            providers: [
                { provide: MODAL_DIALOG_CONTAINER, useValue: this.columnContainerProvider },
                { provide: LOADING_INDICATOR, useValue: this.location.toString() },
            ],
            parent: this.injector,
        });

    private createIndicator = (injector: Injector): ComponentRef<LoadingIndicatorComponent> =>
        this.containerRef.createComponent(LoadingIndicatorComponent, { index: 0, injector });

    private removeComponent = (data: ColumnComponentRemoval): void => {
        const containerContents = this.componentInstances.get(data.component);
        if (containerContents) {
            const componentIndex = this.containerRef.indexOf(containerContents.componentRef.hostView);
            if (componentIndex !== -1) {
                this.containerRef.remove(componentIndex);
            }

            const indicatorIndex = this.containerRef.indexOf(containerContents.indicatorRef.hostView);
            if (indicatorIndex !== -1) {
                this.containerRef.remove(indicatorIndex);
            }

            this.componentInstances.delete(data.component);
        }
    };

    private unregisterComponentInstances(): void {
        this.componentInstances.clear();
    }
}
